import React, { useCallback } from 'react';
import { BranchModel, ServiceModel } from '@/typings/models/Location';
import styles from './index.less';
import { Space, Tag, Tooltip } from 'antd';
import { TeamOutlined, WarningOutlined } from '@ant-design/icons';
import OpenHoursOverview from '@/components/OpenHours/OpenHoursOverview';
import { F, INTL_ACTION, INTL_ENTITY } from '@/utils/intl';
import { useIntl } from 'umi';
import { getLocationAllowDateTimes } from '@/utils/location';
import NavLabel from '@/components/NavLabel';

interface ServiceNavigationProps {
  services: ServiceModel[];
  branch: BranchModel;
  selectedServiceId?: string;
  onSelectService: (id: string) => void;
}

const ServiceNavigation: React.FC<ServiceNavigationProps> = ({
                                                               services,
                                                               branch,
                                                               selectedServiceId,
                                                               onSelectService,
                                                               onAddService,
                                                             }) => {
  const intl = useIntl();
  if (!services?.length) return null;

  const getOpeningHours = useCallback((service: ServiceModel) => {
    return service.calendar?.Availability?.filter(
      (a) => a.allow_day_and_time,
    ).map((a) => a.allow_day_and_time);
  }, []);
  return (
    <div className={styles.serviceNav}>
      <div className={styles.branchSettingsItem}>
        <div
          className={`${styles.serviceItem} ${
            selectedServiceId === 'details' ? styles.active : ''
          }`}
          onClick={() => onSelectService('details')}
        >
          <div className={styles.serviceTypeIcon}>⚙️</div>
          <div>
            <div className={styles.serviceTypeName}>
              <F entity={INTL_ENTITY.BRANCH} action={INTL_ACTION.DETAILS} />
            </div>
            <div>
              <Tag
                color={branch.is_active ? 'success' : 'default'}
                className={styles.statusTag}
              >
                <F
                  id={branch.is_active ? 'common.active' : 'common.inactive'}
                  defaultMessage={branch.is_active ? 'Active' : 'Inactive'}
                />
              </Tag>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.serviceNavLabel}>
        <NavLabel>
          <F id={'entity.service.plural'} />
        </NavLabel>
      </div>
      <div className={styles.servicesList}>
        {services.map((service) => (
          <div
            key={service.id}
            className={`${styles.serviceItem} ${
              selectedServiceId === service.id ? styles.active : ''
            }`}
            onClick={() => onSelectService(service.id)}
          >
            <div className={styles.serviceTypeIcon}>
              {service.service_type?.icon || '📃'}
            </div>
            <div>
              <div className={styles.serviceTypeName}>
                {service.service_type?.name || service.name || <F entity={INTL_ENTITY.SERVICE_TYPE} action={INTL_ACTION.NO} />}
              </div>
              <Space>
                <Tag
                  bordered={false}
                  style={{ marginRight: 0 }}
                  color={service?.agents?.length > 0 ? 'default' : 'red'}
                >
                  <Space size={2}>
                    <TeamOutlined />
                    <span>{service?.agents?.length}</span>
                  </Space>
                </Tag>
                <OpenHoursOverview openingHours={getOpeningHours(service)} />
                {!(service?.agents?.length > 0) &&
                  !(getLocationAllowDateTimes(service)?.length > 0) && (
                    <Tooltip
                      title={intl.formatMessage({
                        id: 'pages.locations.service.missingConfigWarning',
                        defaultMessage:
                          'Agents and Operating Hours are not configured for this service. Please configure them to enable booking.',
                      })}
                    >
                      <WarningOutlined style={{ color: '#faad14' }} />
                    </Tooltip>
                  )}
              </Space>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ServiceNavigation;
